/**
 * Iron golem (vanilla Java): 100 HP, 1.4 x 2.7, speed 0.25, knockback
 * resistance 1, no fall damage, never despawns.
 *  - Attack: damage 15 attribute -> f/2 + nextInt(f) = 7.5..21.5 (normal; the
 *    player-side hit is difficulty scaled: easy 4.75..11.75, hard 11.25..32.25),
 *    target thrown up by +0.4, 10-tick arm swing; cooldown 20 ticks.
 *  - Hunts every hostile except creepers within 16 blocks; angry at a player
 *    who hurt it (or, unless player-built, a villager) for 20-39 s.
 *  - Drops 3-5 iron ingots and 0-2 poppies. Repaired by 25 HP per iron ingot.
 *  - Now and then offers a poppy to a nearby villager (holds it out 20 s).
 */

import { I } from '../../game/itemIds';
import { POPPY_ID } from '../../blocks';
import { stackOf, type Stack } from '../../game/types';
import type { MobCtx } from '../mob';
import type { Mob } from '../mob';
import { GOLEM_PREY, playSound, roll, VMob, type Target } from './ctx';

export const GOLEM_HP = 100;
export const GOLEM_REPAIR = 25;
const ATTACK_ANIM = 10;

/** Vanilla IronGolem.doHurtTarget roll: f/2 + nextInt(floor f). */
export function golemDamage(rnd: () => number): number {
  const f = 15;
  return f / 2 + Math.floor(rnd() * f);
}

/** Crack overlay stage for a health fraction: 0 none, 1 low (<75%), 2 medium (<50%), 3 high (<25%). */
export function crackLevel(health: number, max = GOLEM_HP): number {
  const f = health / max;
  return f < 0.25 ? 3 : f < 0.5 ? 2 : f < 0.75 ? 1 : 0;
}

/**
 * Does a hit on `victim` anger a golem? Golem hurt by the player: always.
 * Villager hurt by the player: only golems the village made (not player-built).
 */
export function angersGolem(victim: 'golem' | 'villager', playerBuilt: boolean): boolean {
  return victim === 'golem' || !playerBuilt;
}

export class IronGolem extends VMob {
  playerBuilt = false;
  anger = 0;
  private attackCd = 0;
  private atk = 0;
  flower = 0;
  private target: Target = null;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'iron_golem', x, y, z, { width: 1.4, height: 2.7, eye: 2.295, health: GOLEM_HP, speed: 0.25 }, rnd);
    this.noFallDamage = true;
    this.knockbackResist = 1;
    this.persistent = true;
  }

  override knockback(): void { /* knockback resistance 1.0 */ }
  override knockbackExtra(): void { /* knockback resistance 1.0 */ }

  override onHurtBy(): void {
    this.provoke(400 + Math.floor(Math.random() * 380));
    playSound('entity.iron_golem.hurt', this);
  }

  onHurtByMob(m: Mob): void {
    if (m.type !== 'creeper' && m.type !== 'iron_golem' && m.type !== 'villager') this.target = m;
  }

  provoke(ticks: number): void { this.anger = Math.max(this.anger, ticks); this.target = 'player'; }

  repair(): boolean {
    if (this.health >= this.maxHealth || !this.alive) return false;
    this.health = Math.min(this.maxHealth, this.health + GOLEM_REPAIR);
    playSound('entity.iron_golem.repair', this);
    return true;
  }

  xpValue(): number { return 0; }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.atk > 0) this.atk--;
    if (this.anger > 0) this.anger--;
    if (this.flower > 0) this.flower--;

    if (this.target === 'player' && (this.anger <= 0 || !ctx.targetable())) this.target = null;
    if (this.target && !this.targetAlive(this.target, ctx)) this.target = null;
    if (this.target && this.target !== 'player' && this.dist2(this.target, ctx) > 24 * 24) this.target = null;
    if (!this.target && this.phase(ctx, 10)) {
      if (this.anger > 0 && ctx.targetable()) this.target = 'player';
      else {
        const m = this.nearest(16, (o) => GOLEM_PREY.has(o.type));
        if (m && this.sees(m, ctx)) this.target = m;
      }
    }

    const t = this.target;
    if (t) {
      this.flower = 0;
      const p = this.tx(t, ctx);
      this.aggressive = true;
      this.faceToward(p.x, p.z);
      this.lookAt(p.x, p.eyeY, p.z);
      this.goToward(p.x, p.z, 1, false);
      if (this.attackCd === 0 && this.inReach(t, ctx, 1.2)) {
        this.attackCd = 20;
        this.atk = ATTACK_ANIM;
        this.strike(t, ctx, golemDamage(ctx.host.rnd), 0.4, 0.4);
        playSound('entity.iron_golem.attack', this);
      }
      return;
    }

    // idle: offer a flower to a villager now and then, else wander slowly
    if (this.flower <= 0 && ctx.host.rnd() < 1 / 8000) {
      if (this.nearest(6, (o) => o.type === 'villager')) this.flower = 400;
    }
    if (this.flower > 0) {
      const v = this.nearest(8, (o) => o.type === 'villager');
      if (v) { this.lookAt(v.x, v.eyeY, v.z); this.faceToward(v.x, v.z); }
      return;
    }
    this.stroll(ctx, 0.6, 3, true);
    this.lookAtNearPlayer(ctx);
  }

  override syncView(host: Parameters<VMob['syncView']>[0]): void {
    this.color = crackLevel(this.health, this.maxHealth) | (this.flower > 0 ? 4 : 0);
    super.syncView(host);
    this.view.attackAnim = this.atk > 0 ? this.atk / ATTACK_ANIM : 0;
  }

  loot(rnd: () => number): Stack[] {
    const out: Stack[] = [stackOf(I.IRON_INGOT, roll(rnd, 3, 5))];
    const p = roll(rnd, 0, 2);
    if (p > 0) out.push(stackOf(POPPY_ID, p));
    return out;
  }

  saveExtra(): unknown { return { pb: this.playerBuilt ? 1 : 0, a: this.anger }; }
  loadExtra(d: unknown): void {
    const o = d as { pb?: number; a?: number };
    if (!o || typeof o !== 'object') return;
    this.playerBuilt = o.pb === 1;
    this.anger = typeof o.a === 'number' ? o.a : 0;
  }
}
